import React, { useState, useEffect } from "react";
import { Mail, Copy, Check, ChevronUp } from "lucide-react";
import { motion } from "framer-motion";

const PrivacyPolicyFB = () => {
  const [copied, setCopied] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const supportEmail = "support@floatkeygames.com";

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(supportEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (error) {
      console.error("Failed to copy email:", error);
    }
  };

  const getScrollY = () => {
    if (typeof window === "undefined") return 0;
    return (
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0
    );
  };

  useEffect(() => {
    const onScroll = () => {
      const y = getScrollY();

      const doc = document.documentElement;
      const body = document.body;
      const scrollHeight =
        (doc.scrollHeight || body.scrollHeight || 0) - window.innerHeight;

      setShowScrollTop(y > 180);

      if (scrollHeight > 0) {
        const progress = Math.min(Math.max(y / scrollHeight, 0), 1);
        setScrollProgress(progress);
      } else {
        setScrollProgress(0);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleScrollTop = () => {
    const topEl = document.getElementById("page-top");
    if (topEl) {
      topEl.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleNavClick = (e, targetId) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-b from-black via-[#050509] to-[#05060a] text-slate-100 overflow-x-hidden scroll-smooth">
      <div id="page-top" />

      <div className="fixed inset-x-0 top-0 z-40 h-[2px] bg-slate-900/80">
        <div
          className="h-full rounded-r-full bg-gradient-to-r from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] transition-[width] duration-200 ease-out"
          style={{ width: `${scrollProgress * 100}%` }}
        />
      </div>

      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute -top-40 left-4 md:left-10 w-72 h-72 md:w-80 md:h-80 rounded-full bg-[radial-gradient(circle_at_center,#8b5cf6_0,transparent_60%)] blur-3xl opacity-70" />
        <div className="absolute top-1/3 right-[-3rem] md:right-0 w-80 h-80 md:w-96 md:h-96 rounded-full bg-[radial-gradient(circle_at_center,#60a5fa_0,transparent_60%)] blur-3xl opacity-70" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(15,23,42,0.97)_0,_transparent_55%)]" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.16] mix-blend-soft-light bg-[linear-gradient(to_right,rgba(148,163,184,0.18)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.18)_1px,transparent_1px)] bg-[size:44px_44px]" />
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-18 relative z-10">
        <header className="relative mb-10 md:mb-14">
          <div className="pointer-events-none absolute right-[-1rem] top-[2rem] w-[260px] md:w-[540px] opacity-[0.16]">
            <img
              src="/assets/images/logo_mobile.png"
              alt=""
              className="w-full h-auto select-none blur-[1.5px]"
            />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(139,92,246,0.45),transparent_70%)] blur-2xl" />
          </div>

          <div className="flex items-center justify-between gap-4 mb-6">
            <button
              type="button"
              className="inline-flex items-center gap-2 text-xs md:text-sm text-slate-300/80 hover:text-slate-50 transition-colors group"
              onClick={() => window.history.back()}
            >
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/5 border border-slate-700/70 group-hover:border-slate-100/80 shadow-[0_0_14px_rgba(15,23,42,0.8)]">
                <span className="block w-2 h-2 -translate-x-[-2px] rotate-45 border-l border-b border-slate-300 group-hover:border-slate-50" />
              </span>
              <span className="uppercase tracking-[0.25em] text-[10px] md:text-[11px]">
                Back
              </span>
            </button>

            <div className="hidden md:flex items-center gap-3 text-[11px] text-slate-400">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-slate-700/80 bg-slate-950/60 backdrop-blur text-[10px] uppercase tracking-[0.22em]">
                <span className="relative flex h-1.5 w-3 items-center">
                  <span className="absolute inset-y-0 left-0 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.9)]" />
                  <span className="absolute inset-y-0 left-1.5 w-1 rounded-full bg-emerald-300/60" />
                </span>
                Online
              </span>
              <span className="w-1 h-1 rounded-full bg-slate-500/80" />
              <span className="uppercase tracking-[0.22em] text-slate-500">
                Legal
              </span>
              <span className="w-1 h-1 rounded-full bg-slate-500/80" />
              <span className="text-slate-300/90">Privacy Policy</span>
            </div>
          </div>

          <div className="inline-flex relative mb-4">
            <motion.div
              className="absolute inset-0 -m-2 rounded-full bg-[#8b5cf6]/30 blur-xl pointer-events-none"
              animate={{
                opacity: [0.18, 0.4, 0.18],
                scale: [0.96, 1.04, 0.96],
              }}
              transition={{
                duration: 3.8,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <motion.p
              className="relative overflow-hidden inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.35em] bg-white/5 text-violet-200/85 border border-violet-300/40 backdrop-blur"
              animate={{ scale: [1, 1.02, 1] }}
              transition={{
                duration: 3.8,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <motion.span
                className="inline-block w-1.5 h-1.5 rounded-full bg-[radial-gradient(circle_at_center,#c4b5fd_0,#a855f7_45%,#60a5fa_100%)] shadow-[0_0_10px_rgba(167,139,250,1)]"
                animate={{
                  opacity: [0.6, 1, 0.6],
                  scale: [1, 1.25, 1],
                }}
                transition={{
                  duration: 3.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
              Privacy Policy
              <span className="inline-flex h-[14px] w-px bg-gradient-to-b from-transparent via-violet-400/80 to-transparent" />
              <span className="text-[10px] tracking-[0.28em] text-slate-300/80">
                Float Block
              </span>

              <motion.span
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(120deg, transparent, rgba(139,92,246,0.12), rgba(192,132,252,0.10), rgba(96,165,250,0.12), transparent)",
                }}
                animate={{ x: ["-160%", "160%"] }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            </motion.p>
          </div>

          <div className="md:flex md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] drop-shadow-[0_0_26px_rgba(129,140,248,0.7)]">
                Float Block – Privacy Policy
              </h1>
              <p className="mt-3 text-sm md:text-base text-slate-300/85 leading-relaxed">
                FloatKeyGames built{" "}
                <span className="font-semibold text-slate-50">
                  Float Block: Bloxorz Puzzle
                </span>{" "}
                as a free app. This Service is provided by FloatKeyGames at no
                cost and is intended for use as is. This page explains how we
                collect, use and protect your personal information when you use
                the app or any related services.
              </p>
              <p className="mt-2 text-sm md:text-base text-slate-400/90 leading-relaxed">
                By choosing to use our Service, you agree to the collection and
                use of information in relation to this Privacy Policy. The terms
                used here have the same meanings as in our Terms &amp;
                Conditions, unless otherwise defined in this document.
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <MetaChip label="Platform" value="Android" />
                <MetaChip label="Status" value="Live" accent="emerald" />
                <MetaChip label="Developer" value="FloatKeyGames" />
              </div>
            </div>
          </div>

          <div className="mt-8 h-px w-full bg-gradient-to-r from-transparent via-[#8b5cf6]/70 to-transparent opacity-80" />
        </header>

        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.7fr)_minmax(0,0.9fr)] gap-10 md:gap-12">
          <div className="space-y-8 text-sm md:text-base text-slate-300 leading-relaxed">
            <motion.section
              id="overview"
              className="group relative rounded-2xl border border-slate-600/60 bg-white/5 px-4 py-4 md:px-5 md:py-5 
             shadow-[0_0_26px_rgba(15,23,42,0.7)] overflow-hidden"
              whileHover={{
                borderColor: "rgba(167,139,250,0.4)", 
              }}
              transition={{ duration: 0.25 }}
            >
              <SectionGlow />
              <div className="space-y-3 relative z-10">
                <SectionTitle index="01" label="Privacy Policy overview" />
                <p>
                  FloatKeyGames built Float Block: Bloxorz Puzzle as a free app.
                  This Service is provided by FloatKeyGames at no cost and is
                  intended for use as is. This page is used to inform visitors
                  regarding our policies with the collection, use, and
                  disclosure of personal information for anyone who decides to
                  use our Service.
                </p>
                <p>
                  If you choose to use our Service, then you agree to the
                  collection and use of information in relation to this policy.
                  The personal information that we collect is used for providing
                  and improving the Service. We will not use or share your
                  information with anyone except as described in this Privacy
                  Policy.
                </p>
                <p>
                  Unless otherwise defined in this Privacy Policy, the terms we
                  use here have the same meanings as in our Terms &amp;
                  Conditions, which are accessible from Float Block: Bloxorz
                  Puzzle.
                </p>
              </div>
            </motion.section>

            <motion.section
              id="changes"
              className="group relative rounded-2xl border border-slate-600/60 bg-white/5 px-4 py-4 md:px-5 md:py-5 
             shadow-[0_0_26px_rgba(15,23,42,0.7)] overflow-hidden"
              whileHover={{
                borderColor: "rgba(167,139,250,0.4)",
              }}
              transition={{ duration: 0.25 }}
            >
              <SectionGlow />
              <div className="space-y-3 relative z-10">
                <SectionTitle
                  index="02"
                  label="What information we collect"
                />
                <p>
                  For the proper functioning of the applications we build or
                  develop, we may collect different types of data. This includes
                  information that helps us understand how the app is used,
                  detect errors, and improve performance and user experience.
                </p>
                <p>
                  In general, the data we collect can include registration data,
                  device and usage information, cookies or similar technologies,
                  and information collected through third-party services
                  integrated into the app.
                </p>
              </div>
            </motion.section>

            <motion.section
              id="data-security"
              className="group relative rounded-2xl border border-slate-600/60 bg-white/5 px-4 py-4 md:px-5 md:py-5 
             shadow-[0_0_26px_rgba(15,23,42,0.7)] overflow-hidden"
              whileHover={{
                borderColor: "rgba(167,139,250,0.4)",
              }}
              transition={{ duration: 0.25 }}
            >
              <SectionGlow />
              <div className="space-y-3 relative z-10">
                <SectionTitle index="03" label="Registration data" />
                <p>
                  Every time you use the Service, in the event of an
                  application error, we may collect data and information
                  (through third-party products) on your phone called
                  registration data.
                </p>
                <p>
                  This registration data may include information such as your
                  device Internet Protocol (&quot;IP&quot;) address, device
                  name, operating system version, app configuration when using
                  the Service, the time and date of your use of the Service, and
                  other statistics.
                </p>
              </div>
            </motion.section>

            <motion.section
              id="third-party"
              className="group relative rounded-2xl border border-slate-600/60 bg-white/5 px-4 py-4 md:px-5 md:py-5 
             shadow-[0_0_26px_rgba(15,23,42,0.7)] overflow-hidden"
              whileHover={{
                borderColor: "rgba(167,139,250,0.4)",
              }}
              transition={{ duration: 0.25 }}
            >
              <SectionGlow />
              <div className="space-y-3 relative z-10">
                <SectionTitle index="04" label="Cookies and third-party tools" />
                <p>
                  Cookies are files with a small amount of data that are
                  commonly used as unique anonymous identifiers. They are sent
                  to your browser from the websites you visit and stored in your
                  device&apos;s internal memory.
                </p>
                <p>
                  This Service does not use cookies explicitly. However, the app
                  may use third-party code and libraries that use cookies to
                  collect information and improve their services. You have the
                  option to accept or reject these cookies and to be notified
                  when a cookie is being sent to your device. If you choose to
                  decline cookies, some parts of the Service may not function
                  properly.
                </p>
                <p>
                  The app also uses third-party services that may collect
                  information used to identify you, including but not limited
                  to:
                </p>
                <ul className="list-none space-y-2">
                  {["Google Play", "Google Analytics", "Firebase", "Unity"].map(
                    (item) => (
                      <li key={item} className="flex items-start gap-2">
                        <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[radial-gradient(circle_at_center,#c4b5fd_0,#a855f7_45%,#60a5fa_100%)] shadow-[0_0_8px_rgba(129,140,248,0.9)] flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    )
                  )}
                </ul>
                <p>
                  We recommend that you review the privacy policies of these
                  providers to understand how they handle your information.
                </p>
              </div>
            </motion.section>

            <motion.section
              id="connectivity"
              className="group relative rounded-2xl border border-slate-600/60 bg-white/5 px-4 py-4 md:px-5 md:py-5 
             shadow-[0_0_26px_rgba(15,23,42,0.7)] overflow-hidden"
              whileHover={{
                borderColor: "rgba(167,139,250,0.4)",
              }}
              transition={{ duration: 0.25 }}
            >
              <SectionGlow />
              <div className="space-y-3 relative z-10">
                <SectionTitle index="05" label="Service providers" />
                <p>
                  We may employ third-party companies and individuals for
                  various reasons related to the Service. For example, to help
                  us operate and provide the app, to perform services on our
                  behalf, to provide additional functionality, or to assist us
                  in analyzing how the Service is used.
                </p>
                <p>
                  These third parties may have access to your personal
                  information in order to perform the tasks assigned to them on
                  our behalf. However, they are obligated not to disclose or use
                  the information for any other purpose.
                </p>
              </div>
            </motion.section>

            <motion.section
              id="device-responsibility"
              className="group relative rounded-2xl border border-slate-600/60 bg-white/5 px-4 py-4 md:px-5 md:py-5 
             shadow-[0_0_26px_rgba(15,23,42,0.7)] overflow-hidden"
              whileHover={{
                borderColor: "rgba(167,139,250,0.4)",
              }}
              transition={{ duration: 0.25 }}
            >
              <SectionGlow />
              <div className="space-y-3 relative z-10">
                <SectionTitle index="06" label="Security of your data" />
                <p>
                  We value your trust in providing us with your personal
                  information, and we strive to use commercially acceptable
                  means to protect it. However, please remember that no method
                  of transmission over the internet or method of electronic
                  storage is 100% secure and reliable, and we cannot guarantee
                  its absolute security.
                </p>
              </div>
            </motion.section>

            <motion.section
              id="liability"
              className="group relative rounded-2xl border border-slate-600/60 bg-white/5 px-4 py-4 md:px-5 md:py-5 
             shadow-[0_0_26px_rgba(15,23,42,0.7)] overflow-hidden"
              whileHover={{
                borderColor: "rgba(167,139,250,0.4)",
              }}
              transition={{ duration: 0.25 }}
            >
              <SectionGlow />
              <div className="space-y-3 relative z-10">
                <SectionTitle index="07" label="Links to other sites" />
                <p>
                  This Service may contain links to other sites. If you click on
                  a third-party link, you will be directed to that site. Note
                  that these external sites are not operated by us.
                </p>
                <p>
                  We strongly advise you to review the Privacy Policy of these
                  websites. We have no control over and assume no
                  responsibility for the content, privacy policies, or practices
                  of any third-party sites or services.
                </p>
              </div>
            </motion.section>

            <motion.section
              id="updates-termination"
              className="group relative rounded-2xl border border-slate-600/60 bg-white/5 px-4 py-4 md:px-5 md:py-5 
             shadow-[0_0_26px_rgba(15,23,42,0.7)] overflow-hidden"
              whileHover={{
                borderColor: "rgba(167,139,250,0.4)",
              }}
              transition={{ duration: 0.25 }}
            >
              <SectionGlow />
              <div className="space-y-3 relative z-10">
                <SectionTitle
                  index="08"
                  label="How we use collected information"
                />
                <p>
                  The data collected in connection with the app can be grouped
                  into two types: data collected directly by us and data
                  collected by third parties.
                </p>
                <p>
                  Data collected by us is used to better understand what our
                  users need and to improve the app by providing the right
                  improvements in each update. This information is processed at
                  a code and aggregate level; we do not access data from a
                  specific individual user. Where applicable, data is encrypted
                  and only the user who owns the information can directly access
                  it.
                </p>
                <p>
                  Data collected by third parties is handled by the services
                  mentioned above (such as Google Play, Google Analytics,
                  Firebase, or Unity) according to their own privacy policies.
                </p>
              </div>
            </motion.section>

            <motion.section
              id="changes-terms"
              className="group relative rounded-2xl border border-slate-600/60 bg-white/5 px-4 py-4 md:px-5 md:py-5 
             shadow-[0_0_26px_rgba(15,23,42,0.7)] overflow-hidden"
              whileHover={{
                borderColor: "rgba(167,139,250,0.4)",
              }}
              transition={{ duration: 0.25 }}
            >
              <SectionGlow />
              <div className="space-y-3 relative z-10">
                <SectionTitle
                  index="09"
                  label="Children’s privacy & policy changes"
                />
                <p>
                  These Services are not directed to anyone under the age of 13.
                  We do not knowingly collect personally identifiable
                  information from children under 13. If we discover that a
                  child under 13 has provided us with personal information, we
                  will immediately delete it from our servers. If you are a
                  parent or guardian and you are aware that your child has
                  provided us with personal information, please contact us so
                  that we can take the necessary actions.
                </p>
                <p>
                  We may update our Privacy Policy from time to time. You are
                  therefore advised to review this page periodically for any
                  changes. We will notify you of any changes by posting the new
                  Privacy Policy on this page.
                </p>
                <p>
                  This Privacy Policy is effective as of{" "}
                  <span className="font-semibold text-slate-100">
                    2020-12-01
                  </span>
                  .
                </p>
              </div>
            </motion.section>

            <motion.section
              id="contact"
              className="group relative rounded-2xl border border-slate-600/60 bg-white/5 px-4 py-4 md:px-5 md:py-5 
             shadow-[0_0_26px_rgba(15,23,42,0.7)] overflow-hidden"
              whileHover={{
                borderColor: "rgba(167,139,250,0.4)",
              }}
              transition={{ duration: 0.25 }}
            >
              <SectionGlow />
              <div className="space-y-3 relative z-10">
                <SectionTitle index="10" label="Contact us" />
                <p>
                  If you have any questions about this Privacy Policy or our
                  Terms &amp; Conditions, do not hesitate to contact us at the
                  email address below.
                </p>

                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={handleCopyEmail}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-600/60 bg-white/5 px-3.5 py-1.5 text-[13px] shadow-[0_0_18px_rgba(15,23,42,0.9)] hover:border-violet-400/80 hover:bg-slate-900 hover:shadow-[0_0_22px_rgba(129,140,248,0.7)] transition-all"
                  >
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-900/90 border border-slate-700/80">
                      <Mail className="w-3.5 h-3.5 text-slate-200" />
                    </span>
                    <span className="font-medium text-slate-50">
                      {supportEmail}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-slate-400">
                      {copied ? (
                        <>
                          <Check className="w-3.5 h-3.5 text-emerald-400" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5 text-slate-400" />
                          Click to copy
                        </>
                      )}
                    </span>
                  </button>
                  <p className="text-[11px] text-slate-500">
                    Clicking the button will copy the support email to your
                    clipboard.
                  </p>
                </div>
              </div>
            </motion.section>
          </div>

          <aside className="space-y-6 md:space-y-8 md:sticky md:top-28 self-start">
            <div className="relative rounded-2xl border border-slate-600/60 bg-white/5  backdrop-blur-xl p-4 md:p-5 shadow-[0_0_26px_rgba(15,23,42,0.9)] overflow-hidden">
              <div className="absolute -top-6 left-4 w-24 h-10 bg-[radial-gradient(circle_at_center,rgba(129,140,248,0.65)_0,transparent_65%)] blur-md opacity-90 pointer-events-none" />
              <div className="inline-flex items-center gap-2 mb-3 relative">
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-slate-950/70 border border-slate-600/70 text-[10px] uppercase tracking-[0.26em] text-slate-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-[radial-gradient(circle_at_center,#c4b5fd_0,#a855f7_45%,#60a5fa_100%)] shadow-[0_0_8px_rgba(167,139,250,0.9)]" />
                  Overview
                </span>
              </div>
              <ul className="space-y-2 text-[13px] md:text-sm text-slate-300">
                {[
                  ["overview", "01. Privacy Policy overview"],
                  ["changes", "02. Information we collect"],
                  ["data-security", "03. Registration data"],
                  ["third-party", "04. Cookies & third-party tools"],
                  ["connectivity", "05. Service providers"],
                  ["device-responsibility", "06. Security of your data"],
                  ["liability", "07. Links to other sites"],
                  ["updates-termination", "08. Use of collected data"],
                  ["changes-terms", "09. Children’s privacy & changes"],
                  ["contact", "10. Contact"],
                ].map(([id, label]) => (
                  <li key={id}>
                    <button
                      type="button"
                      onClick={(e) => handleNavClick(e, id)}
                      className="inline-flex items-center justify-between gap-2 w-full group hover:text-slate-50 transition-colors"
                    >
                      <span className="inline-flex items-center gap-2 text-left">
                        <span className="relative flex items-center">
                          <span className="h-px w-5 bg-gradient-to-r from-transparent via-[#8b5cf6]/80 to-transparent group-hover:via-[#c4b5fd] transition-colors" />
                          <span className="absolute -left-1 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[radial-gradient(circle_at_center,#c4b5fd_0,#a855f7_45%,#60a5fa_100%)] shadow-[0_0_8px_rgba(167,139,250,0.9)] opacity-80 group-hover:opacity-100" />
                        </span>
                        <span>{label}</span>
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.24em] text-slate-500 group-hover:text-violet-300/90">
                        Jump
                      </span>
                    </button>
                  </li>
                ))}
              </ul>

              <p className="mt-4 text-[11px] text-slate-500 leading-relaxed">
                Tip: Click a section to jump directly, or use your browser
                search (<span className="font-mono">Ctrl+F</span>) to find
                specific terms.
              </p>
            </div>

            <section
              id="last-update"
              className="relative rounded-2xl border border-slate-600/60 bg-white/5 px-4 py-4 md:px-5 md:py-5 shadow-[0_0_26px_rgba(15,23,42,0.7)] overflow-hidden"
            >
              <div className="pointer-events-none absolute -top-10 -right-6 w-28 h-28 rounded-full bg-[radial-gradient(circle_at_center,rgba(129,140,248,0.7)_0,transparent_60%)] blur-xl opacity-80" />
              <div className="relative space-y-2 z-10">
                <p className="text-[11px] uppercase tracking-[0.26em] text-slate-500 mb-1 flex items-center gap-2">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[radial-gradient(circle_at_center,#c4b5fd_0,#a855f7_45%,#60a5fa_100%)] shadow-[0_0_10px_rgba(167,139,250,1)]" />
                  Last update
                </p>
                <p className="text-sm font-medium text-slate-100">
                  01 Dec 2020
                </p>
                <p className="text-[11px] md:text-[12px] text-slate-400 leading-relaxed">
                  This Privacy Policy is effective as of{" "}
                  <span className="font-semibold text-slate-200">
                    2020-12-01
                  </span>
                  . Continued use of the app implies acceptance of any future
                  updates to this document.
                </p>
              </div>
            </section>
          </aside>
        </div>
      </div>

      {showScrollTop && (
        <button
          type="button"
          onClick={handleScrollTop}
          className="
            fixed bottom-6 right-5 md:right-8
            h-11 w-11
            flex items-center justify-center
            rounded-full
            border border-slate-700/80
            bg-slate-950/95
            text-slate-200
            shadow-[0_0_22px_rgba(15,23,42,0.95)]
            hover:border-violet-400/80 hover:bg-slate-900/95 hover:text-slate-50
            hover:-translate-y-0.5
            active:scale-95
            transition-all
            cursor-pointer
            z-50
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/80 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950
          "
          aria-label="Back to top"
        >
          <ChevronUp className="w-4 h-4" />
        </button>
      )}
    </main>
  );
};

const SectionTitle = ({ index, label }) => (
  <div className="flex items-center gap-3">
    <div className="flex items-center gap-2">
      <span className="inline-flex items-center justify-center rounded-full border border-slate-700/80 bg-slate-950/70 px-2 py-0.5 text-[10px] uppercase tracking-[0.26em] text-slate-300">
        {index}
      </span>
      <span className="h-6 w-px bg-gradient-to-b from-[#8b5cf6]/90 via-slate-600 to-transparent" />
    </div>
    <h2 className="text-lg md:text-xl font-semibold text-slate-50">
      {label}
    </h2>
  </div>
);

const SectionGlow = () => (
  <div className="pointer-events-none absolute inset-x-0 -top-24 h-28 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-out bg-[radial-gradient(circle_at_top,rgba(129,140,248,0.35),transparent_60%)]" />
);

const MetaChip = ({ label, value, accent = "violet" }) => {
  const base =
    accent === "emerald"
      ? "from-emerald-400/80 to-emerald-300/50 shadow-[0_0_18px_rgba(16,185,129,0.45)]"
      : "from-[#8b5cf6]/80 via-[#c084fc]/75 to-[#60a5fa]/70 shadow-[0_0_18px_rgba(129,140,248,0.45)]";

  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/70 bg-slate-950/75 px-2.5 py-1 text-[11px]">
      <span
        className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${base}`}
      />
      <span className="uppercase tracking-[0.16em] text-slate-400">
        {label}
      </span>
      <span className="text-[11px] text-slate-100 font-medium">{value}</span>
    </div>
  );
};

export default PrivacyPolicyFB;
